import startCase from 'lodash/startCase';
import { useMemo } from 'react';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../filters/hooks/usePrimaryFilterState';
import { useViewDateGranularity } from '../../filters/hooks/useViewDateGranularity';
import { isSegmentedFilter } from '../../filters/types/filters';
import { formatFilterDisplay } from '../../filters/utils/formatFilterValue';
import type { PptCoverInfo } from '../Chart/chart-menu/data-download/ppt-export/ppt-exporter';
import { useFilterTags } from '../FilterBar/useFilterTags';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';

const MAX_ENTITY_NAMES = 5;

export const useExportCover = (): PptCoverInfo => {
  const { config: pageConfig } = usePageConfig();
  const { config: tabContent, dateFilterId } = useTabContentConfig();
  const { primaryFilter } = usePrimaryFilterState(tabContent.primaryFilter?.id);
  const { filterTags } = useFilterTags();
  const effectiveFilters = useEffectiveFilters();
  const { currencySymbol, exchangeRate } = useCurrencyPreference();
  // Dates as the filter chips and the download file names show them.
  const dateGranularity = useViewDateGranularity();
  const formatContext = { currencySymbol, exchangeRate, dateGranularity };

  const primaryFilterId = tabContent.primaryFilter?.id;
  const title = tabContent.title?.trim() || pageConfig.name;

  const parameters: { label: string; value: string }[] = [];

  const entityNames = (primaryFilter?.primarySelections ?? [])
    .map((selection) => selection.name)
    .filter((name): name is string => !!name?.trim());
  if (primaryFilterId && entityNames.length > 0) {
    const shown = entityNames.slice(0, MAX_ENTITY_NAMES).join(', ');
    const remaining = entityNames.length - MAX_ENTITY_NAMES;
    parameters.push({
      label: startCase(primaryFilterId.toLowerCase()),
      value: remaining > 0 ? `${shown} +${remaining} more` : shown,
    });
  }

  // The dates the data covers, also while the date filter is at its default
  // and has no tag. Listed early: the PPT cover only fits the first rows.
  const dateFilter = effectiveFilters.find(
    (filter) => filter.id === dateFilterId
  );
  const dataDates = dateFilter
    ? formatFilterDisplay(dateFilter, formatContext)
    : '';
  if (dateFilter && dataDates) {
    parameters.push({ label: dateFilter.name, value: dataDates });
  }

  for (const { filter, disabled } of filterTags) {
    if (disabled || filter.id === dateFilterId) continue;
    const value = formatFilterDisplay(
      isSegmentedFilter(filter) ? filter.wrappedFilter : filter,
      formatContext
    );
    if (!value.trim()) continue;
    parameters.push({ label: filter.name, value });
  }

  const parametersKey = JSON.stringify(parameters);
  // biome-ignore lint/correctness/useExhaustiveDependencies: parametersKey stands in for parameters
  return useMemo(() => ({ title, parameters }), [title, parametersKey]);
};
